export type ActivityState = 'ouvert' | 'a-venir' | 'complet'

export interface Activity {
  id: string
  name: string
  audience: string
  place: string
  schedule: string
  price: string
  availability: string
  state: ActivityState
  opensOn?: string
  image: string
}

export interface Registration {
  id: string
  activity: string
  participant: string
  initials: string
  schedule: string
  place: string
  nextSession: string
}

export interface Facility {
  id: string
  name: string
  capacity: string
  availability: string
  price: string
  place: string
}

export interface Equipment {
  id: string
  name: string
  detail: string
  availability: string
  price: string
}

export const atnaChips = [
  'Trouver une activité',
  'Mes inscriptions',
  'Réserver un espace',
  'Louer de l’équipement',
]

/** Enfant inscrit par défaut dans le flux ATNA d'inscription. */
export const inscriptionChild = { name: 'Emma', age: '9 ans', initials: 'EB' }

/** Allergies déjà au dossier d'Emma, reprises à chaque inscription. */
export const declaredAllergies = [
  {
    id: 'amoxicilline',
    name: 'Amoxicilline',
    kind: 'Médicament',
    detail: 'Réaction cutanée · déclarée le 12 mars 2025',
  },
  {
    id: 'arachides',
    name: 'Arachides',
    kind: 'Alimentaire',
    detail: 'Auto-injecteur d’épinéphrine dans son sac',
  },
]

export const inscriptionCategories = ['Natation', 'Soccer', 'Patinage', 'Arts plastiques', 'Musique']

/** Créneau proposé par une autre famille pour un échange d'inscription. */
export interface SwapOffer {
  id: string
  registrationId: string
  day: string
  time: string
  place: string
  group: string
  offeredBy: string
  note?: string
  /** Compatibilité du créneau avec l'horaire de la famille, évaluée par ATNA. */
  fit: 'ideal' | 'possible' | 'conflit'
  fitLabel: string
}

export const swapOffers: SwapOffer[] = [
  {
    id: 'swap-1',
    registrationId: 'r-natation',
    day: 'Mercredi',
    time: '16 h 30 à 17 h 15',
    place: 'Piscine régionale — Baie-Comeau',
    group: 'Natation — Junior · groupe B',
    offeredBy: 'Famille V. · 2 séances déjà faites',
    note: 'Même transport organisé par la Municipalité',
    fit: 'ideal',
    fitLabel: 'Aucun conflit dans votre horaire',
  },
  {
    id: 'swap-2',
    registrationId: 'r-natation',
    day: 'Vendredi',
    time: '17 h 45 à 18 h 30',
    place: 'Piscine régionale — Baie-Comeau',
    group: 'Natation — Junior · groupe D',
    offeredBy: 'Famille L. · échange direct',
    fit: 'possible',
    fitLabel: 'Retour tard sur la 138',
  },
  {
    id: 'swap-3',
    registrationId: 'r-natation',
    day: 'Samedi',
    time: '8 h 15 à 9 h',
    place: 'Piscine régionale — Baie-Comeau',
    group: 'Natation — Junior · groupe A',
    offeredBy: 'Famille C. · échange direct',
    fit: 'conflit',
    fitLabel: 'Chevauche l’ouverture du centre de tri',
  },
  {
    id: 'swap-4',
    registrationId: 'r-yoga',
    day: 'Mardi',
    time: '7 h 30 à 8 h 30',
    place: 'Centre communautaire',
    group: 'Yoga du matin · groupe 2',
    offeredBy: 'Famille R. · échange direct',
    fit: 'ideal',
    fitLabel: 'Aucun conflit dans votre horaire',
  },
  {
    id: 'swap-5',
    registrationId: 'r-yoga',
    day: 'Jeudi',
    time: '12 h à 13 h',
    place: 'Halte du quai (extérieur)',
    group: 'Yoga du midi',
    offeredBy: 'Famille D. · échange direct',
    fit: 'possible',
    fitLabel: 'Sur l’heure du dîner',
  },
]

export interface ActivitySlot {
  id: string
  category: string
  name: string
  day: string
  time: string
  place: string
  audience: string
  price: string
  spots: string
  /** Créneau mis en avant par ATNA (aucun conflit à l'horaire). */
  recommended?: boolean
  note?: string
}

export const availableSlots: ActivitySlot[] = [
  {
    id: 'natation-mer',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Mercredi',
    time: '16 h 30 à 17 h 15',
    place: 'Piscine régionale — Baie-Comeau',
    audience: '8 à 10 ans',
    price: '68 $ · 10 séances',
    spots: '4 places restantes',
    recommended: true,
    note: 'Transport organisé par la Municipalité',
  },
  {
    id: 'natation-sam',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Samedi',
    time: '10 h à 10 h 45',
    place: 'Piscine régionale — Baie-Comeau',
    audience: '8 à 10 ans',
    price: '68 $ · 10 séances',
    spots: '1 place restante',
  },
  {
    id: 'soccer-mar',
    category: 'Soccer',
    name: 'Soccer récréatif',
    day: 'Mardi',
    time: '17 h 30 à 18 h 45',
    place: 'Terrain du village',
    audience: '8 à 10 ans',
    price: '45 $ · saison',
    spots: '8 places restantes',
    recommended: true,
    note: 'À cinq minutes de votre adresse',
  },
  {
    id: 'soccer-sam',
    category: 'Soccer',
    name: 'Soccer récréatif',
    day: 'Samedi',
    time: '9 h à 10 h 15',
    place: 'Terrain du village',
    audience: '8 à 10 ans',
    price: '45 $ · saison',
    spots: '3 places restantes',
  },
  {
    id: 'patin-jeu',
    category: 'Patinage',
    name: 'Patinage — Initiation',
    day: 'Jeudi',
    time: '17 h à 18 h',
    place: 'Patinoire extérieure',
    audience: '7 à 10 ans',
    price: '30 $ · 12 séances',
    spots: '5 places restantes',
    recommended: true,
    note: 'Équipement prêté par la Municipalité',
  },
  {
    id: 'arts-mer',
    category: 'Arts plastiques',
    name: 'Atelier d’arts plastiques',
    day: 'Mercredi',
    time: '18 h à 19 h 15',
    place: 'Centre communautaire',
    audience: '8 à 12 ans',
    price: '35 $ · 8 ateliers',
    spots: '7 places restantes',
    recommended: true,
    note: 'Matériel inclus',
  },
  {
    id: 'musique-lun',
    category: 'Musique',
    name: 'Éveil musical — guitare',
    day: 'Lundi',
    time: '17 h 15 à 18 h',
    place: 'Centre communautaire',
    audience: '8 à 12 ans',
    price: '80 $ · 10 cours',
    spots: '2 places restantes',
    recommended: true,
    note: 'Instrument prêté pour la session',
  },
]

export const activities: Activity[] = [
  {
    id: 'club-lecture',
    name: 'Club de lecture jeunesse',
    audience: '8 à 12 ans',
    place: 'Bibliothèque municipale',
    schedule: 'Mercredi · 18 h',
    price: 'Gratuit',
    availability: '6 places disponibles',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a2858b27-8254-4ffc-98f5-bf2103bc4d3d.jpg',
  },
  {
    id: 'patinage',
    name: 'Patinage libre',
    audience: 'Tous',
    place: 'Patinoire extérieure',
    schedule: 'Fins de semaine · dès décembre',
    price: 'Gratuit',
    availability: 'Saison à venir',
    state: 'a-venir',
    opensOn: '15 décembre',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/793c8eb8-cf34-4510-adfc-3778b264e964.jpg',
  },
  {
    id: 'mise-en-forme',
    name: 'Mise en forme 50 ans et plus',
    audience: '50 ans et plus',
    place: 'Centre communautaire',
    schedule: 'Lundi et mercredi · 9 h 30',
    price: '30 $',
    availability: 'Complet',
    state: 'complet',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/fe1ad829-8957-437c-a55f-47f9c5e28205.jpg',
  },
  {
    id: 'natation-junior',
    name: 'Natation — Junior',
    audience: '8 à 12 ans',
    place: 'Piscine régionale — Baie-Comeau',
    schedule: 'Samedi · 10 h',
    price: '68 $',
    availability: '4 places disponibles',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6a1ec671-9e1b-4e2e-9311-d05e4e59a61f.jpg',
  },
]

/** Inscription validée par la Municipalité, en attente du paiement. */
export const readiness = {
  activity: 'Natation — Junior',
  participant: 'Emma',
  session: 'Mercredi · 16 h 30 · Piscine régionale',
  reference: '#INS-2026-3312',
  amount: '68,00 $',
  payBefore: 'vendredi 25 septembre, 23 h 59',
  checks: [
    { id: 'carte', label: 'Carte numérique', ok: true },
    { id: 'residence', label: 'Statut de résidence', ok: true },
    { id: 'participant', label: 'Profil du participant', ok: true },
    { id: 'place', label: 'Place réservée', ok: true },
  ],
}

/** Carte enregistrée au dossier, proposée par défaut au paiement en ligne. */
export const savedCard = { brand: 'Visa', last4: '4127', expiry: '09/29' }

/** Étapes du paiement en argent comptant, au comptoir. */
export const cashSteps = [
  {
    title: 'Générez votre code de paiement',
    detail: 'Un code unique est rattaché à l’inscription d’Emma. Présentez-le au comptoir.',
  },
  {
    title: 'Présentez-vous au bureau municipal',
    detail: 'Bureau municipal, 28 route 138 · lundi au jeudi, 8 h à 16 h 30 ; vendredi, 8 h à 12 h.',
  },
  {
    title: 'Payez en argent comptant',
    detail: 'Montant exact recommandé. Le personnel encaisse et confirme l’inscription sur place.',
  },
  {
    title: 'Recevez votre reçu',
    detail: 'Le reçu est déposé dans Mon profil → Mes factures et la place est définitivement confirmée.',
  },
]

export const participants = [
  { id: 'charlotte', name: 'Charlotte', initials: 'CB', detail: 'Titulaire' },
  { id: 'emma', name: 'Emma', initials: 'EG', detail: '10 ans' },
  { id: 'leo', name: 'Léo', initials: 'LG', detail: '9 ans' },
]

export const registrations: Registration[] = [
  {
    id: 'r-natation',
    activity: 'Natation — Junior',
    participant: 'Emma',
    initials: 'EG',
    schedule: 'Samedi · 10 h',
    place: 'Piscine régionale — Baie-Comeau',
    nextSession: 'Prochaine séance : samedi 26 septembre',
  },
  {
    id: 'r-yoga',
    activity: 'Yoga du matin',
    participant: 'Charlotte',
    initials: 'CB',
    schedule: 'Mercredi · 7 h 30',
    place: 'Centre communautaire',
    nextSession: 'Prochaine séance : mercredi 23 septembre',
  },
]

export const followUps = [
  'Le transport vers la piscine régionale part du bureau municipal à 9 h 15 ce samedi.',
  'Les inscriptions au niveau suivant ouvrent lundi — Emma y sera admissible.',
]

export const facilities: Facility[] = [
  {
    id: 'salle-communautaire',
    name: 'Salle communautaire',
    capacity: '20 à 40 personnes',
    availability: 'Samedi 13 h à 17 h',
    price: '60 $ / 4 h',
    place: 'Centre communautaire',
  },
  {
    id: 'gymnase',
    name: 'Gymnase de l’école',
    capacity: 'Sports d’équipe',
    availability: 'Samedi 9 h à 12 h',
    price: '25 $ / h',
    place: 'École du village',
  },
  {
    id: 'patinoire',
    name: 'Patinoire extérieure',
    capacity: 'Glace complète',
    availability: 'Dimanche 18 h à 19 h',
    price: 'Gratuit pour les résidents',
    place: 'Terrain du village',
  },
]

export const equipment: Equipment[] = [
  {
    id: 'raquettes',
    name: 'Raquettes à neige',
    detail: '4 paires · adultes',
    availability: 'Dès le 15 décembre',
    price: '10 $ / jour',
  },
  { id: 'patins', name: 'Patins', detail: 'Tailles 1 à 12', availability: 'Disponible', price: 'Gratuit' },
  {
    id: 'communautaire',
    name: 'Matériel communautaire',
    detail: 'Tables, chapiteaux, chaises',
    availability: 'Sur réservation',
    price: 'Selon le matériel',
  },
]

/** Créneau réservable d'un espace municipal. */
export interface FacilitySlot {
  id: string
  facilityId: string
  day: string
  time: string
  duration: string
  amount: string
  left: string
}

export const facilitySlots: FacilitySlot[] = [
  {
    id: 'salle-sam-19',
    facilityId: 'salle-communautaire',
    day: 'Samedi 19 septembre',
    time: '13 h à 17 h',
    duration: '4 h',
    amount: '60,00 $',
    left: 'Dernier bloc de la journée',
  },
  {
    id: 'salle-dim-20',
    facilityId: 'salle-communautaire',
    day: 'Dimanche 20 septembre',
    time: '10 h à 14 h',
    duration: '4 h',
    amount: '60,00 $',
    left: 'Disponible',
  },
  {
    id: 'salle-sam-26',
    facilityId: 'salle-communautaire',
    day: 'Samedi 26 septembre',
    time: '18 h à 22 h',
    duration: '4 h',
    amount: '75,00 $',
    left: 'Tarif de soirée',
  },
  {
    id: 'gym-sam-9',
    facilityId: 'gymnase',
    day: 'Samedi 19 septembre',
    time: '9 h à 11 h',
    duration: '2 h',
    amount: '50,00 $',
    left: 'Disponible',
  },
  {
    id: 'gym-sam-11',
    facilityId: 'gymnase',
    day: 'Samedi 19 septembre',
    time: '11 h à 12 h',
    duration: '1 h',
    amount: '25,00 $',
    left: 'Dernière heure libre',
  },
  {
    id: 'gym-mer-23',
    facilityId: 'gymnase',
    day: 'Mercredi 23 septembre',
    time: '19 h à 21 h',
    duration: '2 h',
    amount: '50,00 $',
    left: 'Disponible',
  },
  {
    id: 'glace-dim-20',
    facilityId: 'patinoire',
    day: 'Dimanche 20 décembre',
    time: '18 h à 19 h',
    duration: '1 h',
    amount: '0,00 $',
    left: 'Glace complète',
  },
  {
    id: 'glace-sam-26',
    facilityId: 'patinoire',
    day: 'Samedi 26 décembre',
    time: '19 h à 20 h',
    duration: '1 h',
    amount: '0,00 $',
    left: 'Dernier bloc du week-end',
  },
]

/** Conditions rappelées avant de confirmer la location d'un espace. */
export const facilityConditions = [
  'Présentez votre carte numérique à l’arrivée : elle déverrouille l’accès au local réservé.',
  'Annulation sans frais jusqu’à 48 h avant le créneau.',
  'Le local doit être remis dans son état initial à la fin du bloc réservé.',
]

/** Point et plage de cueillette d'un équipement. */
export interface PickupWindow {
  id: string
  equipmentId: string
  place: string
  day: string
  time: string
  note?: string
}

export const pickupWindows: PickupWindow[] = [
  {
    id: 'raquettes-communautaire',
    equipmentId: 'raquettes',
    place: 'Centre communautaire',
    day: 'Samedi 19 décembre',
    time: '9 h à 10 h',
    note: 'Retour le lendemain avant 17 h',
  },
  {
    id: 'raquettes-bureau',
    equipmentId: 'raquettes',
    place: 'Bureau municipal (28, route 138)',
    day: 'Dimanche 20 décembre',
    time: '10 h à 11 h',
  },
  {
    id: 'patins-patinoire',
    equipmentId: 'patins',
    place: 'Patinoire extérieure',
    day: 'Samedi 19 décembre',
    time: '13 h à 14 h',
    note: 'Prêt gratuit pour les résidents',
  },
  {
    id: 'patins-communautaire',
    equipmentId: 'patins',
    place: 'Centre communautaire',
    day: 'Dimanche 20 décembre',
    time: '11 h à 12 h',
  },
  {
    id: 'communautaire-garage',
    equipmentId: 'communautaire',
    place: 'Garage municipal (route 138)',
    day: 'Vendredi 18 septembre',
    time: '8 h 30 à 11 h',
    note: 'Transport à la charge du citoyen',
  },
  {
    id: 'communautaire-centre',
    equipmentId: 'communautaire',
    place: 'Centre communautaire',
    day: 'Vendredi 18 septembre',
    time: '15 h à 16 h 30',
  },
]
